import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useHttpClient, useNetworkStatus } from '../../contexts';
import { useReducerStorage as useReducer, useToast } from '../../hooks';
import { Organization } from './organization.models';
import {
  initState,
  organizationReducer,
  OrganizationState,
} from './organization.reducer';

interface OrganizationContextProps {
  organization: Organization | null;
  isLoading: boolean;
  isLoaded: boolean;
  isUpdatingLogo: boolean;
  error: string | null;
  fetchOrganization: (organizationId: string) => Promise<void>;
  updateOrganization: (
    organizationId: string,
    organization: Partial<Organization>
  ) => Promise<Organization>;
  uploadOrganizationLogo: (organizationId: string, file: File) => Promise<void>;
  updateOrganizationRatings: (
    organizationId: string,
    ratingsPayload: {
      rating: number;
      provider: string;
      ratingCount: number;
      url: string;
      name: string;
    }
  ) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextProps | undefined>(
  undefined
);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<OrganizationState>;
}>;

export const OrganizationProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    organizationReducer,
    { ...initState, ...initialState },
    {
      key: 'airship-cache:organization',
      select: (s) => ({ organization: s.organization }),
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const fetchOrganization: OrganizationContextProps['fetchOrganization'] =
    useCallback(
      async (organizationId: string) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }

        dispatch({ type: 'FETCH_ORGANIZATION' });
        try {
          const organization = await httpClient.get<Organization>(
            `/organizations/${organizationId}`
          );
          dispatch({
            type: 'FETCH_ORGANIZATION_SUCCESS',
            payload: { organization },
          });
        } catch (error) {
          dispatch({
            type: 'FETCH_ORGANIZATION_FAILURE',
            payload: { error: `Failed to fetch organization: ${error}` },
          });
        }
      },
      [httpClient, isOnline]
    );

  const updateOrganization: OrganizationContextProps['updateOrganization'] =
    useCallback(
      async (organizationId: string, payload: Partial<Organization>) => {
        dispatch({ type: 'UPDATE_ORGANIZATION' });
        try {
          const organization = await httpClient.put<Organization>(
            `/organizations/${organizationId}`,
            payload
          );
          dispatch({
            type: 'UPDATE_ORGANIZATION_SUCCESS',
            payload: { organization },
          });

          return organization;
        } catch (error) {
          dispatch({
            type: 'UPDATE_ORGANIZATION_FAILURE',
            payload: { error: `Failed to update organization: ${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const uploadOrganizationLogo: OrganizationContextProps['uploadOrganizationLogo'] =
    useCallback(
      async (organizationId: string, file: File) => {
        dispatch({ type: 'UPDATE_ORGANIZATION_LOGO' });
        try {
          const metadataResponse = await httpClient.post<{
            preSignedUrl: string;
            filekeyId: string;
            fileKey: string;
          }>(`/organizations/${organizationId}/logo`, {
            filename: file.name,
            contentType: file.type,
            uploadType: 'ORGANIZATION_LOGO',
          });

          await fetch(metadataResponse.preSignedUrl, {
            body: file,
            method: 'PUT',
          });

          dispatch({ type: 'UPDATE_ORGANIZATION_LOGO_SUCCESS' });
        } catch (error) {
          dispatch({
            type: 'UPDATE_ORGANIZATION_LOGO_FAILURE',
            payload: { error: `Failed to upload organization logo: ${error}` },
          });
        }
      },
      [httpClient]
    );

  const updateOrganizationRatings: OrganizationContextProps['updateOrganizationRatings'] =
    useCallback(
      async (
        organizationId: string,
        ratingsPayload: {
          rating: number;
          provider: string;
          ratingCount: number;
          url: string;
          name: string;
        }
      ) => {
        dispatch({ type: 'UPDATE_ORGANIZATION' });
        try {
          const updatedOrganization = await httpClient.put<Organization>(
            `/organizations/${organizationId}/update-organization-ratings`,
            ratingsPayload
          );
          dispatch({
            type: 'UPDATE_ORGANIZATION_SUCCESS',
            payload: { organization: updatedOrganization },
          });
        } catch (error) {
          dispatch({
            type: 'UPDATE_ORGANIZATION_FAILURE',
            payload: {
              error: `Failed to update organization ratings: ${error}`,
            },
          });
        }
      },
      [httpClient]
    );

  return (
    <OrganizationContext.Provider
      value={{
        fetchOrganization,
        updateOrganization,
        uploadOrganizationLogo,
        updateOrganizationRatings,
        organization: state.organization,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isUpdatingLogo: state.isUpdatingLogo,
        error: state.error,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within a OrganizationProvider'
    );
  }

  return context;
};
